// One shared answer to "can this account send or read email at all?", and the
// one sentence the whole dashboard uses to say so.
//
// Both signals live on the user document. `hasMsalToken` is written true only
// when the Microsoft connect flow finishes, and `msalReconnectRequired` is
// raised the moment the saved connection stops working. Until this file existed
// nothing on the dashboard read either one, so a campaign could be launched
// into a mailbox the product could not open, and the advisor was told nothing.

export const MAILBOX_NOT_CONNECTED_MESSAGE =
  'Your mailbox is not connected. SiteSift cannot send or read any email until you connect it.';

export const CONNECT_MAILBOX_LABEL = 'Connect mailbox';

export const CONNECT_MAILBOX_PATH = '/connect-email';

export function isMailboxConnected(userData) {
  if (!userData) return false;
  if (userData.hasMsalToken !== true) return false;
  return !userData.msalReconnectRequired;
}

// The one rule for "may this screen let a campaign be launched?", shared by
// every path that shows the launch step. Only a read that FINISHED and said
// "not connected" holds Start: while it is pending, or outside the provider,
// the launch step behaves exactly as it always has, so nothing flashes a block
// at an advisor whose mailbox is fine. Both launch paths call this, so neither
// can drift into its own idea of "connected".
export function mailboxConnectedForLaunch(mailboxConnection) {
  if (!mailboxConnection) return true;
  return Boolean(mailboxConnection.loading || mailboxConnection.connected);
}

// The reasons the send path writes onto a blocked item, in the advisor's words.
// Anything not listed here is a reason she cannot act on, so it goes to support
// rather than being guessed at on screen.
const MAILBOX_BLOCKED_REASONS = new Set([
  'msal_not_connected',
  'reauth_required'
]);

const BLOCKED_REASON_SENTENCES = {
  // `not_allowed` is the sending allowlist, not the mailbox: an account that has
  // reconnected but is not switched on yet must not be sent round the connect flow again.
  not_allowed: 'your account is not switched on for sending yet. Contact support.',
  msal_not_connected: 'your mailbox is not connected.',
  reauth_required: 'your mailbox is not connected.',
  automation_disabled: 'sending is paused for your account.',
  invalid_campaign_launch: 'this campaign could not be checked before sending.'
};

const UNKNOWN_BLOCKED_SENTENCE = 'SiteSift did not record a reason. Contact support.';

export function describeBlockedOutbox({ count = 0, reason = '', guidance = '' } = {}) {
  const blockedCount = Number(count) || 0;
  if (blockedCount < 1) return null;

  const normalizedReason = String(reason || '').trim();
  const storedGuidance = String(guidance || '').trim();
  const sentence = storedGuidance
    || BLOCKED_REASON_SENTENCES[normalizedReason]
    || UNKNOWN_BLOCKED_SENTENCE;

  const subject = blockedCount === 1 ? '1 email was not sent' : `${blockedCount} emails were not sent`;

  return {
    message: `${subject}: ${sentence}`,
    needsMailboxConnection: MAILBOX_BLOCKED_REASONS.has(normalizedReason)
  };
}
